import {
  Button,
  IconButton,
  HStack,
  Stack,
  Text,
  Image,
  useBreakpointValue,
  Box,
  Spinner,
  VStack,
  useToast,
  Progress,
  useClipboard,
  Divider,
} from "@chakra-ui/react";
import { useEffect, useState, useCallback, useRef, useMemo } from "react";
import { FiCopy, FiEdit2, FiExternalLink } from "react-icons/fi";

import { FaCloudUploadAlt, FaWarehouse } from "react-icons/fa";

import actions from "../actions";
import { KYDAdminEventDetail } from "@common/interfaces/KYDEvent";
import { Outlet, useParams, useLocation, matchPath } from "react-router-dom";
import ReactS3Uploader from "react-s3-uploader";

import { ModalType, EditModalType, showSuccessToast } from "../utils";
import "react-datepicker/dist/react-datepicker.css";

import dayjs from "dayjs";
import timezone from "dayjs/plugin/timezone";
import utc from "dayjs/plugin/utc";
import customParseFormat from "dayjs/plugin/customParseFormat";
import relativeTime from "dayjs/plugin/relativeTime";

import { LocalizationProvider } from "@mui/x-date-pickers";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import { useDialog } from "../Common/Dialog";
import { useLoading, useProgress } from "./hooks/form";
import VenueDisplayHeader from "./VenueDisplayHeader";
import {
  DesktopSideBar,
  MobileNavBar,
} from "./EventDetail/Nav/EventDetailsNavigation";

dayjs.extend(timezone);
dayjs.extend(utc);
dayjs.extend(customParseFormat);
dayjs.extend(relativeTime);

const EventDetail = ({
  setAppModalType,
}: {
  setAppModalType: (state: ModalType, description: string) => void;
}) => {
  const [kydEvent, setKydEvent] = useState<KYDAdminEventDetail | null>(null);
  const { event_id } = useParams();
  const [modalType, setModalType] = useState(EditModalType.INACTIVE);
  const [dirtyText, setDirtyText] = useState<any>({});
  const [loading, updateLoading] = useLoading({
    tickettype: false,
    promocode: false,
    create_affiliates: false,
    blocking: false,
    event: false,
    metrics: false,
  });
  const [progress, updateProgress] = useProgress({
    poster: false,
  });

  const isMobile = useBreakpointValue({
    base: true,
    md: false,
  });

  const location = useLocation();

  const hideEventDetailHeaderUrlList = [
    `/events/:event_id/settings`,
    `events/:event_id/reporting`,
  ];
  const shouldHideHeader = useMemo(() => {
    const isThere = hideEventDetailHeaderUrlList.some((url) => {
      return matchPath(url, location.pathname);
    });
    return isThere;
  }, [location]);

  const { onCopy } = useClipboard(kydEvent?.preview_url);
  const toast = useToast();
  const dialog = useDialog();
  const uploadRef = useRef<HTMLInputElement>(null);

  const hasVenue = !!kydEvent?.venues?.at(0);

  const updateDirtyText = useCallback((update: any | string) => {
    if (update === "all") {
      setDirtyText({});
    } else {
      setDirtyText((l: any) => {
        const keys = Object.keys(update);
        keys.forEach((key) => {
          if (update[key] === null) {
            delete l[key];
          } else {
            l[key] = update[key];
          }
        });
        return { ...l };
      });
    }
  }, []);

  const fetchEvent = useCallback(
    async (eventId?: string, options?: any) => {
      if (!options || (options && !options.silent)) {
        updateLoading("init", true);
      }

      let _;
      try {
        if (!eventId) {
          throw Error("NO event ID provided");
        }
        _ = await actions.ADMIN.fetchEventWithSections(eventId);
        setKydEvent(_);
        dayjs.tz.setDefault(_.timezone);

        if (
          _ &&
          _.latest_update &&
          _.latest_update.status === "PENDING" &&
          !options
        ) {
          updateLoading("init", false);
          updateLoading("blocking", true);
          await pollForEventUpdateStatus();
          updateLoading("blocking", false);
        }
      } catch (err) {
        dialog({
          text: err.message,
          actions: [
            {
              type: "confirm",
              text: "Got it",
              onClick: () => {
                window.location.assign("/");
              },
            },
          ],
        });
      }
      updateLoading("init", false);
      return _;
    },
    [updateLoading]
  );

  useEffect(() => {
    if (event_id) {
      fetchEvent(event_id);
    }
  }, [event_id, fetchEvent]);

  const pollForEventUpdateStatus = useCallback(async () => {
    console.log("Poll Event Status");
    const searchParams = new URLSearchParams(window.location.search);
    const updateId = searchParams.get("update_id");
    const sleep = (milliseconds: number) => {
      return new Promise((resolve) => setTimeout(resolve, milliseconds));
    };

    if (updateId) {
      let attempts = 0;
      let didFind = false;

      do {
        console.log("Attempt: ", attempts);
        let serverEventUpdate: any;
        let failed = false;
        try {
          serverEventUpdate = await actions.ADMIN.fetchEventUpdate(
            event_id!,
            updateId
          );
        } catch (err) {
          console.log(err);
          failed = true;
        }

        if (
          !failed &&
          (!serverEventUpdate || serverEventUpdate.status === "COMPLETE")
        ) {
          didFind = true;
          searchParams.delete("update_id");

          const newUrl =
            window.location.protocol +
            "//" +
            window.location.host +
            window.location.pathname +
            "?" +
            searchParams.toString();
          window.history.pushState({ path: newUrl }, "", newUrl);

          if (
            serverEventUpdate &&
            serverEventUpdate.status === "COMPLETE" &&
            serverEventUpdate.error
          ) {
            console.log(
              "Found Update: ",
              JSON.stringify(serverEventUpdate, null, 2)
            );
            dialog({
              text: serverEventUpdate.error.message || serverEventUpdate.error,
            });
            return false;
          } else {
            await fetchEvent(event_id!, { silent: true });
          }
          break;
        } else {
          await sleep(3000);
          attempts += 1;
        }
      } while (attempts < 15);

      return didFind;
    }

    return false;
  }, [event_id, fetchEvent]);

  const portalRef = useRef<HTMLDivElement>(null);
  return (
    <LocalizationProvider dateAdapter={AdapterDayjs}>
      {isMobile && (
        <Box
          top={0}
          left={0}
          right={0}
          bottom={0}
          h="full"
          w="full"
          position="absolute"
          ref={portalRef}
        ></Box>
      )}
      <Stack
        spacing={0}
        padding={0}
        direction={["column", "row"]}
        width={"full"}
        h="full"
        overflow={isMobile ? "auto" : "hidden"}
        // minH="100%"
        flexGrow={1}
        position="relative"
      >
        {!isMobile && (
          <Box
            top={0}
            left={0}
            right={0}
            bottom={0}
            h="full"
            w="full"
            position="absolute"
            ref={portalRef}
          ></Box>
        )}
        <DesktopSideBar
          show={!isMobile}
          nav={kydEvent?.nav}
          isLoading={loading.event}
        />
        <Stack
          spacing={{ base: "8", lg: "6" }}
          transform="translate3d(0,0,0)"
          width="100%"
          // @ts-ignore
          style={{ "::-webkit-scrollbar": { display: "none" } }}
          // height="100%"
        >
          {loading["init"] && (
            <VStack>
              <Spinner />
              <Text>Loading event...</Text>
            </VStack>
          )}

          {kydEvent && (
            <Stack
              spacing={10}
              minWidth={["100%", "initial"]}
              width="100%"
              overflow="auto"
              p={[4, 8]}
              display="flex"
              alignItems="center"
              justifyContent="flex-start"
            >
              <MobileNavBar
                show={isMobile}
                nav={kydEvent?.nav}
                isLoading={loading.event}
              />
              <Outlet
                context={{ portalRef }}
                key={kydEvent?.venues?.at(0)?.id}
              />
            </Stack>
          )}
        </Stack>
      </Stack>
    </LocalizationProvider>
  );
};

export default EventDetail;
